import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { RequestLine } from './types';
import { isSkipped } from '../../../utils/requestLines';
import { skippedCardId } from './SkippedQuestionCard';

// "1 result needs your input — a clarification was skipped; the part is not
// selected yet." Driven by the order's current ledger, so it disappears as soon
// as the skipped line is answered. Clicking it scrolls to the skipped question.

interface Props {
  lines: readonly RequestLine[];
  t: (key: string, opts?: any) => string;
}

const SkippedLinesBar: React.FC<Props> = ({ lines, t }) => {
  const skipped = lines.filter(l => isSkipped(l));
  if (skipped.length === 0) return null;
  const goTo = () => {
    const qid = skipped[0].question_id;
    if (!qid) return;
    // The newest card for that question (an older message may carry one too).
    const cards = document.querySelectorAll(`#${CSS.escape(skippedCardId(qid))}`);
    const el = cards[cards.length - 1] as HTMLElement | undefined;
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  };
  return (
    <div
      role="status"
      data-testid="skipped-lines-bar"
      onClick={goTo}
      style={{
        position: 'sticky', top: 0, zIndex: 5, display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 20px', background: '#fdf4d8', borderBottom: '1px solid #f1dfa6',
        fontSize: 14, color: '#3d3b45', cursor: 'pointer',
      }}
    >
      <AlertTriangle size={15} color="#9a6700" style={{ flexShrink: 0 }} />
      <span>
        <strong style={{ fontWeight: 700, color: '#201e1a' }}>
          {skipped.length === 1
            ? t('chat.multiPart.skippedBarCountOne', { defaultValue: '1 result needs your input' })
            : t('chat.multiPart.skippedBarCountMany', { defaultValue: '{{count}} results need your input', count: skipped.length })}
        </strong>
        {' — '}
        {skipped.length === 1
          ? t('chat.multiPart.skippedBarWhyOne', { defaultValue: 'a clarification was skipped; the part is not selected yet.' })
          : t('chat.multiPart.skippedBarWhyMany', { defaultValue: 'clarifications were skipped; those parts are not selected yet.' })}
      </span>
    </div>
  );
};

export default SkippedLinesBar;
